import { useListCirclesQuery } from "@/api/circles-api";
import { useHomepage } from "@/api/homepage";
import { useGetPrograms } from "@/api/program";
import { MountWhenVisible } from "@/components/MountWhenVisible";
import { AppShell } from "@/components/Navbar";
import { SEOHead } from "@/components/SEO/SEOHead";
import { useFormStore } from "@/contexts/paymentFlowContext";
import { TitleSection } from "@/pages/home/sections/title-section";
import useCurrencyStore from "@/store/currencyStore";
import { useLocation } from "@tanstack/react-router";
import clsx from "clsx";
import { Loader2 } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const Partners = lazy(() => import("./sections/Partners").then((m) => ({ default: m.Partners })));
const ProgramListing = lazy(() => import("./sections/ProgramListing"));
const EFBCircle = lazy(() => import("@/components/Cricle/EFBCircle"));
const CreateCircleEntrypoint = lazy(() => import("@/components/Cricle/CreateCircleEntrypoint"));
const ImpactSection = lazy(() =>
  import("@/pages/program/sections/impact").then((m) => ({ default: m.ImpactSection })),
);
const Categories = lazy(() => import("./sections/Categories"));
const Testimonials = lazy(() => import("./sections/Testimonials"));
const JoinCircleModal = lazy(() => import("@/components/Cricle/JoinCircleModal"));

function SectionSuspenseFallback() {
  return (
    <div
      className="flex min-h-[200px] w-full items-center justify-center py-8"
      role="status"
      aria-busy="true"
    >
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden />
      <span className="sr-only">Loading</span>
    </div>
  );
}

export const HomePage = () => {
  const { i18n } = useTranslation();
  const donationSocket = useFormStore((state) => state.donationSocket);
  const { currency } = useCurrencyStore();
  const { data: homepageData } = useHomepage({ lang: i18n.language });
  const { data: circles } = useListCirclesQuery({ is_owned_by_efb_directly: true });
  const { search: params } = useLocation();
  const { checkoutStep, selectedAmount, inputAmount, reset } = useFormStore();
  const [isScrolled, setIsScrolled] = useState(false);
  const progRef = useRef<HTMLDivElement>(null);
  let logos = [];
  let testimonials = [];
  let kpis = {
    number_of_donations: 0,
    years_of_service: 0,
    number_of_beneficiaries: 0,
  };
  if (homepageData?.length) {
    kpis.number_of_beneficiaries = homepageData[0].number_of_beneficiaries;
    kpis.number_of_donations = homepageData[0].number_of_donations;
    kpis.years_of_service = homepageData[0].years_of_service;
    logos = homepageData[0].homepage_logo;
    testimonials = homepageData[0].testimonials;
  }

  useGetPrograms({
    ordering: "rank",
    page_size: "3",
    lang: i18n.language,
  });

  useGetPrograms({
    ordering: "rank",
    page_size: "3",
    lang: i18n.language,
  });

  useEffect(() => {
    if (params.response_code) {
      checkoutStep();
      if (donationSocket && donationSocket.readyState === WebSocket.OPEN) {
        const amount = selectedAmount === inputAmount ? selectedAmount : inputAmount;
        donationSocket.send(JSON.stringify({ amount, currency }));
      }
    }
  }, [params]);

  useEffect(() => {
    if (!params.response_code) {
      reset();
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (progRef.current) {
        const rect = progRef.current.getBoundingClientRect();
        if (rect.bottom <= 0) {
          setIsScrolled(true);
        } else {
          setIsScrolled(false);
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <SEOHead pageKey="home" />
      <a href="#mainContent" aria-label="Skip to main content"></a>
      <div className="relative">
        <img
          src="/Frame-hero.svg"
          alt="Decorative Background SVG"
          className="absolute left-0 top-0 z-10 h-full w-full object-cover"
          width={1920}
          height={1080}
          fetchPriority="high"
        />
        <AppShell
          navbarClass={clsx(
            "fixed top-0 -z-10 w-full bg-background-second opacity-0 transition-opacity duration-300 ease-in-out",
            { "opacity-100 z-10": isScrolled },
          )}
        />

        <AppShell navbarClass="w-full h-[50vh] bg-background-second md:h-auto" />

        <div ref={progRef} className="relative h-[50vh] min-h-[300px] bg-background-second">
          <TitleSection />
        </div>
      </div>

      <main
        id="mainContent"
        className="mx-4 mt-4 flex max-h-full max-w-full flex-col items-center justify-center bg-background md:mx-6 md:mt-6"
      >
        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <Partners Items={logos} />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <ProgramListing />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <div className="mb-8 grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
              {circles?.data?.results?.map((circle) => (
                <EFBCircle key={circle.id} circle={circle} />
              ))}
              <CreateCircleEntrypoint />
            </div>
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <ImpactSection
              NumberOfBeneficiaries={kpis.number_of_beneficiaries}
              YearsOfService={kpis.years_of_service}
              NumberOfDonations={kpis.number_of_donations}
              showLink={true}
              dynamicColor={"hsla(45, 18%, 87%, 1)"}
            />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <Categories />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <Testimonials
              testimonials={testimonials}
              size={"5xl"}
              dynamicColor={"hsla(45, 18%, 87%, 1)"}
            />
          </Suspense>
        </MountWhenVisible>
        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <Partners Items={logos} />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <ProgramListing />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <div className="mb-8 grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
              {circles?.data?.results?.map((circle) => (
                <EFBCircle key={circle.id} circle={circle} />
              ))}
              <CreateCircleEntrypoint />
            </div>
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <ImpactSection
              NumberOfBeneficiaries={kpis.number_of_beneficiaries}
              YearsOfService={kpis.years_of_service}
              NumberOfDonations={kpis.number_of_donations}
              showLink={true}
              dynamicColor={"hsla(45, 18%, 87%, 1)"}
            />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <Categories />
          </Suspense>
        </MountWhenVisible>

        <MountWhenVisible className="w-full">
          <Suspense fallback={<SectionSuspenseFallback />}>
            <Testimonials
              testimonials={testimonials}
              size={"5xl"}
              dynamicColor={"hsla(45, 18%, 87%, 1)"}
            />
          </Suspense>
        </MountWhenVisible>
      </main>
      <MountWhenVisible className="w-full">
        <Suspense fallback={null}>
          <JoinCircleModal />
        </Suspense>
      </MountWhenVisible>
      <MountWhenVisible className="w-full">
        <Suspense fallback={null}>
          <JoinCircleModal />
        </Suspense>
      </MountWhenVisible>
    </>
  );
};
